/**
 * useStripOverflow - the rails' order strip past its width (2026-10-21: "the top turn tracker does not scroll";
 * the canvas's TurnStrip artboard; utils/stripScroll holds the geometry). The ONE owner of the strip's scroll.
 *
 * Hung on two elements by CALLBACK refs - the seats' scroller (`scrollerRef`, the tablist) and the frame round it
 * with the ‹ › buttons (`frameRef`) - so every listener follows its element when React makes a new one (starting
 * combat moves the rows into another parent):
 * - `state`: whether seats are hidden and on which side - the ‹ › buttons show only when they are, each disabled
 *   at its end, and the stylesheet fades the hidden side. Read again after every render (a longer name or a new
 *   mark widens the seats without resizing the scroller), on scroll, and on resize;
 * - the WHEEL scrolls sideways: a native, non-passive listener (React's is passive), which takes the wheel only
 *   when the strip can move that way - otherwise the page keeps it;
 * - `step(dir)`: one seat per press;
 * - the ACTING seat is centred as the turn moves, and the TARGET is brought clear of the fades whenever it changes;
 * - a SEAT dragged near either edge (or over ‹ ›) scrolls the strip, so a reorder can reach a hidden seat. Only a
 *   drag that started on a seat: a file or a token dragged across the strip leaves it alone.
 * A strip with nothing hidden does nothing at all; the column, which never attaches the refs, nothing either.
 *
 * EITHER AXIS (2026-10-22, plan muster): the muster scrolls down its column the same way. `axis: 'y'` reads tops and
 * bottoms where the strip reads lefts and rights (the geometry is axis-neutral); `seat` says what one seat is. The
 * wheel and the drag edges are the strip's alone: a column scrolls natively, and nothing is dragged in it.
 *
 * @param {string | null} activeTab - the targeted combatant's id: brought into view when it changes
 * @param {string | null} actingKey - the acting row's key: centred when it changes
 * @param {{ axis?: 'x' | 'y', seat?: string }} [options] - the strip's by default
 * @returns {{ scrollerRef: (el: HTMLElement | null) => void, frameRef: (el: HTMLElement | null) => void,
 *   state: { overflow: boolean, atStart: boolean, atEnd: boolean, after: number }, step: (dir: 1 | -1) => void }}
 *   (`after`: the seats wholly past the end - the bars over the map count them, "▾ N MORE")
 */
import { useCallback, useEffect, useRef, useState } from 'react';
import { centreOn, hiddenAfter, intoView, overflowOf, stepBy, wheelDelta } from '../utils/stripScroll';

const FITS = { overflow: false, atStart: true, atEnd: true, after: 0 };
/** How near an edge a dragged seat scrolls the strip, and how far each dragover moves it, px. */
const DRAG_EDGE = 32;
const DRAG_STEP = 8;

/** What each axis reads off an element: its scroll numbers and its client-space edges, and how it is scrolled. */
const AXES = {
  x: {
    scrollOf: (el) => ({ pos: el.scrollLeft, size: el.scrollWidth, view: el.clientWidth }),
    edgesOf: (el) => {
      const r = el.getBoundingClientRect();
      return { start: r.left, end: r.right };
    },
    scrollBy: (el, d) => {
      el.scrollLeft += d;
    },
  },
  y: {
    scrollOf: (el) => ({ pos: el.scrollTop, size: el.scrollHeight, view: el.clientHeight }),
    edgesOf: (el) => {
      const r = el.getBoundingClientRect();
      return { start: r.top, end: r.bottom };
    },
    scrollBy: (el, d) => {
      el.scrollTop += d;
    },
  },
};

const STRIP = { axis: 'x', seat: '[role="tab"]' };

const same = (a, b) =>
  a.overflow === b.overflow && a.atStart === b.atStart && a.atEnd === b.atEnd && a.after === b.after;

/** The seats wholly past the end, counted only while some are hidden there (it measures every seat). */
function afterOf(scroller, ax, seat, at) {
  if (at.atEnd) return 0;
  return hiddenAfter([...scroller.querySelectorAll(seat)].map(ax.edgesOf), ax.edgesOf(scroller));
}

/** The overflow: after every render, and on the scroller's scroll and resize. */
function useOverflowState(scroller, ax, seat) {
  const [state, setState] = useState(FITS);
  const read = useCallback(() => {
    if (!scroller) return;
    const at = overflowOf(ax.scrollOf(scroller));
    const next = { ...at, after: afterOf(scroller, ax, seat, at) };
    setState((s) => (same(s, next) ? s : next));
  }, [scroller, ax, seat]);
  // No dependency list on purpose: seat widths change with names and marks, which resize nothing.
  useEffect(read);
  useEffect(() => {
    if (!scroller) return undefined;
    scroller.addEventListener('scroll', read, { passive: true });
    const Observer = globalThis.ResizeObserver;
    const observer = Observer ? new Observer(read) : null;
    observer?.observe(scroller);
    return () => {
      scroller.removeEventListener('scroll', read);
      observer?.disconnect();
    };
  }, [scroller, read]);
  return scroller ? state : FITS;
}

/** The wheel sideways over the seats: a native listener, so it can cancel the page's scroll. */
function useWheel(scroller) {
  useEffect(() => {
    if (!scroller) return undefined;
    const onWheel = (e) => {
      const delta = wheelDelta(e, AXES.x.scrollOf(scroller));
      if (!delta) return;
      e.preventDefault();
      scroller.scrollLeft += delta;
    };
    scroller.addEventListener('wheel', onWheel, { passive: false });
    return () => scroller.removeEventListener('wheel', onWheel);
  }, [scroller]);
}

/** A seat dragged near an edge of the frame, or over its buttons, scrolls the seats. */
function useDragEdges(frame, scroller) {
  useEffect(() => {
    if (!frame || !scroller) return undefined;
    let seatDrag = false;
    const onStart = (e) => {
      seatDrag = !!e.target?.closest?.('[role="tab"]');
    };
    const onEnd = () => {
      seatDrag = false;
    };
    const onOver = (e) => {
      if (!seatDrag) return;
      const { start, end } = AXES.x.edgesOf(scroller);
      if (e.clientX < start + DRAG_EDGE) scroller.scrollLeft -= DRAG_STEP;
      else if (e.clientX > end - DRAG_EDGE) scroller.scrollLeft += DRAG_STEP;
    };
    frame.addEventListener('dragstart', onStart);
    frame.addEventListener('dragend', onEnd);
    frame.addEventListener('drop', onEnd);
    frame.addEventListener('dragover', onOver);
    return () => {
      frame.removeEventListener('dragstart', onStart);
      frame.removeEventListener('dragend', onEnd);
      frame.removeEventListener('drop', onEnd);
      frame.removeEventListener('dragover', onOver);
    };
  }, [frame, scroller]);
}

/** The acting seat centred as the turn moves; the target clear of the fades when it changes. */
function useKeepInView(scroller, activeTab, actingKey, ax) {
  useEffect(() => {
    const seat = scroller?.querySelector('[data-status="active"]');
    if (seat) ax.scrollBy(scroller, centreOn(ax.edgesOf(seat), ax.edgesOf(scroller)));
  }, [scroller, actingKey, ax]);
  useEffect(() => {
    const seat = activeTab ? scroller?.querySelector(`[data-combatant-id="${activeTab}"]`) : null;
    if (seat) ax.scrollBy(scroller, intoView(ax.edgesOf(seat), ax.edgesOf(scroller)));
  }, [scroller, activeTab, ax]);
}

export default function useStripOverflow(activeTab, actingKey, options = STRIP) {
  const { axis, seat } = { ...STRIP, ...options };
  const ax = AXES[axis];
  const across = axis === 'x';
  const [scroller, setScroller] = useState(/** @type {HTMLElement | null} */ (null));
  const [frame, setFrame] = useState(/** @type {HTMLElement | null} */ (null));
  /** @type {(el: HTMLElement | null) => void} */
  const scrollerRef = setScroller;
  /** @type {(el: HTMLElement | null) => void} */
  const frameRef = setFrame;
  const state = useOverflowState(scroller, ax, seat);
  useWheel(across ? scroller : null);
  useDragEdges(across ? frame : null, scroller);
  useKeepInView(scroller, activeTab, actingKey, ax);
  const latest = useRef(scroller);
  latest.current = scroller;
  const step = useCallback(
    (dir) => {
      const el = latest.current;
      if (!el) return;
      const seats = [...el.querySelectorAll(seat)].map(ax.edgesOf);
      ax.scrollBy(el, stepBy(seats, ax.edgesOf(el), dir));
    },
    [seat, ax],
  );
  return { scrollerRef, frameRef, state, step };
}
